/** This file should not export selectors to
 * other selector files to prevent circular dependency */
import { createSelector } from 'reselect';
import { appContext } from '../../../../../web/models/getHelp/appContext';
import { EmeraldUserType } from '../../../../../web/models/getHelp/emeraldUserType';
import { SourceApps } from '../../../../models/constants/constants';
import {
  IAppState,
  IRequestDetailsState,
  ModalitySectionState,
  ServiceRequestPageType,
  UITabView,
} from '../../../../models/reduxState';
import {
  currentUITabViewSelector,
  eduProductIdSelector,
  getCurrentServiceRequestPageType,
  isBATenantSelector,
  isGetHelpSelector,
  isOcvEnabledSelector,
  isRequestCreationSelector,
  isSignedInSelector,
  isThirdPartySelector,
  isW365CSelector,
  modalitySectionStateSelector,
  showModalitiesStickyFooterBRSSelector,
  sourceAppSelector,
} from '../../../app/selectors/contactUsSelector';
import { RequestModality } from '../../models/requestModels';
import {
  areNotesValidSelector,
  isAttachmentsUpdatedSelector,
  isC2CPhoneEditingEnabledSelector,
  isContactsUpdatedSelector,
  requestDetailsSelector,
  isRequestDetailsPhoneEditEnabledSelector,
} from './requestDetailsSelectors';
import {
  getIsRequestSaveBtnEnabled,
  isDFCRequestSelector,
  isRequestClosedSelector,
  isRequestDetailsEditModeSelector,
  modalitySelector,
  severitySelector,
} from './requestSelector';
import { InputField } from '../../../../../web/models/typings/inputFields';
import { EduConfig } from '../../../../../web/edu/eduConfig';
import { getUserContext } from '../../../../../web/utils/statelessUtil';

const getIsEmailFieldDisabled = (
  currentUITabView: UITabView,
  sourceApp: SourceApps,
  modality: RequestModality
): boolean => {
  if (!!currentUITabView) {
    switch (currentUITabView) {
      case UITabView.requestCreation:
        return (
          sourceApp !== SourceApps.SignUp && modality == RequestModality.Chat
        );
      case UITabView.requestDetails:
        return false;
      default:
        break;
    }
  }
};

const getIsTitleFieldDisabled = (
  currentUITabView: UITabView,
  modality: RequestModality,
  isBATenant: boolean,
): boolean => {
  if (!!currentUITabView) {
    switch (currentUITabView) {
      case UITabView.requestCreation:
        return (
          isBATenant && (modality == RequestModality.Email || modality == RequestModality.Phone)
        );
      case UITabView.requestDetails:
        return true;
    }
  }
  return false;
};

const getIsUserDescriptionFieldDisabled = (
  currentUITabView: UITabView,
  modality: RequestModality,
  isBATenant: boolean,
): boolean => {
  if (!!currentUITabView) {
    switch (currentUITabView) {
      case UITabView.requestCreation:
        return (
          isBATenant && (modality != RequestModality.Email && modality != RequestModality.Phone)
        );
      case UITabView.requestDetails:
        return true;
    }
  }
  return false;
};

export const isEmailFieldDisabledSelector = createSelector(
  [currentUITabViewSelector, sourceAppSelector, modalitySelector],
  getIsEmailFieldDisabled
);

export const isTitleFieldDisabledSelector = createSelector(
  [currentUITabViewSelector, modalitySelector, isBATenantSelector],
  getIsTitleFieldDisabled
);

export const isUserDescriptionFieldDisabledSelector = createSelector(
  [currentUITabViewSelector, modalitySelector, isBATenantSelector],
  getIsUserDescriptionFieldDisabled
);

export const isPhoneEditingModeSelector = createSelector(
  [
    isSignedInSelector,
    isRequestCreationSelector,
    isRequestDetailsEditModeSelector,
    modalitySelector,
    isC2CPhoneEditingEnabledSelector,
    isRequestDetailsPhoneEditEnabledSelector,
    showModalitiesStickyFooterBRSSelector,
  ],
  (
    isSignedIn: boolean,
    isRequestCreation: boolean,
    isRequestDetailsEditMode: boolean,
    modality: RequestModality,
    isC2CPhoneEditingEnabled: boolean,
    isRequestDetailsPhoneEditEnabled: boolean,
    showModalitiesStickyFooterBRS: boolean
  ) =>
    /* This selector is used to switch between editable mode and request details mode,
    if stickyFooterBRS == false, we'll only show editable mode
    since we don't have request details if stickyFooter experiment isn't turned on
    */
    !showModalitiesStickyFooterBRS ||
    (isSignedIn &&
      (isRequestCreation ||
        (isRequestDetailsPhoneEditEnabled && isRequestDetailsEditMode &&
          (modality != RequestModality.C2C ||
            (modality == RequestModality.C2C && isC2CPhoneEditingEnabled)))))
);

export const isEmailEditingModeSelector = createSelector(
  [
    isSignedInSelector,
    isRequestCreationSelector,
    isRequestDetailsEditModeSelector,
    showModalitiesStickyFooterBRSSelector,
  ],
  (
    isSignedIn: boolean,
    isRequestCreation: boolean,
    isRequestDetailsEditMode: boolean,
    showModalitiesStickyFooterBRS: boolean
  ) =>
    /* This selector is used to switch between editable mode and request details mode,
    if stickyFooterBRS == false, we'll only show editable mode
    since we don't have request details if stickyFooter experiment isn't turned on
    */
    !showModalitiesStickyFooterBRS ||
    (isRequestCreation && isSignedIn) ||
    (isRequestDetailsEditMode && isSignedIn)
);

const getIsPhoneNumberValid = (
  currentUITabView: UITabView,
  requestDetails: IRequestDetailsState,
  modalityState: ModalitySectionState,
  showModalitiesStickyFooterBRS: boolean
): boolean => {
  if (!showModalitiesStickyFooterBRS) {
    return modalityState.isPhoneNumberValid;
  }
  if (!!currentUITabView) {
    switch (currentUITabView) {
      case UITabView.requestCreation:
        return modalityState.isPhoneNumberValid;
      case UITabView.requestDetails:
        return requestDetails.isPhoneNumberValid;
      default:
        return false;
    }
  }
};

export const isPhoneNumberValidSelector = createSelector(
  [
    currentUITabViewSelector,
    requestDetailsSelector,
    modalitySectionStateSelector,
    showModalitiesStickyFooterBRSSelector,
  ],
  getIsPhoneNumberValid
);

const getIsEmailValid = (
  currentUITabView: UITabView,
  requestDetails: IRequestDetailsState,
  modalityState: ModalitySectionState,
  showModalitiesStickyFooterBRS: boolean
): boolean => {
  // This BRS is used to switch between editable mode and request details mode, if stickyFooterBRS == false, we'll only show editable mode
  if (!showModalitiesStickyFooterBRS) {
    return modalityState.isEmailValid;
  } else if (!!currentUITabView) {
    switch (currentUITabView) {
      case UITabView.requestCreation:
        return modalityState.isEmailValid;
      case UITabView.requestDetails:
        return requestDetails.isEmailValid;
      default:
        return false;
    }
  }
};

export const isEmailValidSelector = createSelector(
  [
    currentUITabViewSelector,
    requestDetailsSelector,
    modalitySectionStateSelector,
    showModalitiesStickyFooterBRSSelector,
  ],
  getIsEmailValid
);

const getIsTitleValid = (
  currentUITabView: UITabView,
  modalityState: ModalitySectionState,
  showModalitiesStickyFooterBRS: boolean
): boolean => {
  // This BRS is used to switch between editable mode and request details mode, if stickyFooterBRS == false, we'll only show editable mode
  if (!showModalitiesStickyFooterBRS) {
    return modalityState.isTitleValid;
  } else if (!!currentUITabView) {
    switch (currentUITabView) {
      case UITabView.requestCreation:
        return modalityState.isTitleValid;
      default:
        return false;
    }
  }
};

export const isTitleValidSelector = createSelector(
  [
    currentUITabViewSelector,
    modalitySectionStateSelector,
    showModalitiesStickyFooterBRSSelector,
  ],
  getIsTitleValid
);

const businessImpactConfirmed = (
  currentUITabView: UITabView,
  requestDetails: IRequestDetailsState,
  modalityState: ModalitySectionState,
  showModalitiesStickyFooterBRS: boolean
): boolean => {
  if (!showModalitiesStickyFooterBRS) {
    return modalityState.businessImpactConfirmed;
  } else if (!!currentUITabView) {
    switch (currentUITabView) {
      case UITabView.requestCreation:
        return modalityState.businessImpactConfirmed;
      case UITabView.requestDetails:
        return requestDetails.businessImpactConfirmed;
      default:
        return false;
    }
  }
};

export const businessImpactConfirmedSelector = createSelector(
  [
    currentUITabViewSelector,
    requestDetailsSelector,
    modalitySectionStateSelector,
    showModalitiesStickyFooterBRSSelector,
  ],
  businessImpactConfirmed
);

const customerAvailabilityConfirmed = (
  currentUITabView: UITabView,
  requestDetails: IRequestDetailsState,
  modalityState: ModalitySectionState,
  showModalitiesStickyFooterBRS: boolean
): boolean => {
  if (!showModalitiesStickyFooterBRS) {
    return modalityState.customerAvailabilityConfirmed;
  } else if (!!currentUITabView) {
    switch (currentUITabView) {
      case UITabView.requestCreation:
        return modalityState.customerAvailabilityConfirmed;
      case UITabView.requestDetails:
        return requestDetails.customerAvailabilityConfirmed;
      default:
        return false;
    }
  }
};

export const customerAvailabilityConfirmedSelector = createSelector(
  [
    currentUITabViewSelector,
    requestDetailsSelector,
    modalitySectionStateSelector,
    showModalitiesStickyFooterBRSSelector,
  ],
  customerAvailabilityConfirmed
);

export const isRequestSaveBtnEnabledSelector = createSelector(
  [
    isPhoneNumberValidSelector,
    isEmailValidSelector,
    isContactsUpdatedSelector,
    isAttachmentsUpdatedSelector,
    areNotesValidSelector,
    severitySelector,
    businessImpactConfirmedSelector,
    customerAvailabilityConfirmedSelector,
  ],
  getIsRequestSaveBtnEnabled
);

export const isSeverityEditingModeSelector = createSelector(
  [
    isSignedInSelector,
    isRequestCreationSelector,
    isRequestDetailsEditModeSelector,
    showModalitiesStickyFooterBRSSelector,
  ],
  (
    isSignedIn: boolean,
    isRequestCreation: boolean,
    isRequestDetailsEditMode: boolean,
    showModalitiesStickyFooterBRS: boolean
  ) =>
    !showModalitiesStickyFooterBRS ||
    (isRequestCreation && isSignedIn) ||
    (isRequestDetailsEditMode && isSignedIn)
);

export const showAgentModalitiesButtonClickedSelector = createSelector(
  modalitySectionStateSelector,
  (modalityState: ModalitySectionState) => modalityState.showAgentModalitiesButtonClicked
)

export const showStickyFooterSelector = (state: IAppState, isRequestClosed: boolean): boolean => {
  const isThirdParty: boolean = isThirdPartySelector(state);
  const isW365C: boolean = isW365CSelector(state);
  const showFooterBrs: boolean =
    state.ContactState.SelfHelpAlchemyState.showModalitiesStickyFooter;
  const isAlchemyQueryCallReturned: boolean =
    state.ContactState.SelfHelpAlchemyState.isAlchemyQueryCompleted;
  const isSignedIn: boolean = state.ContactState.ModalitySectionState.isSignedIn;
  const currentUITabView: UITabView = currentUITabViewSelector(state);
  const isGetHelp: boolean = isGetHelpSelector(state);
  // common check for all tabs to disable footer
  if (
    isW365C ||
    isThirdParty ||
    !showFooterBrs ||
    (isGetHelp && (appContext.emeraldUserType === EmeraldUserType.Commercial || !appContext.appSupportsAuth))
  ) {
    return false;
  }
  // tabView specific checks
  switch (currentUITabView) {
    case UITabView.searchHelp:
      // the only requirement is that the user searches
      return isAlchemyQueryCallReturned;
    case UITabView.requestHistory:
      return false;
    case UITabView.requestCreation:
      // show if the user is not signed in, i.e - the tab was changed by unauthSignInLink or we need to show ocv button
      return isAlchemyQueryCallReturned && (!isSignedIn || isSignedIn && isOcvEnabledSelector(state));
    case UITabView.requestDetails:
      const isRequestEditingEnabled = state.ContactState.RequestDetailsState.isRequestEditingEnabled;
      return isSignedIn && isRequestEditingEnabled && !isRequestClosed && !isDFCRequestSelector(state);
    default:
      return false;
  }
};

// Todo: Kept here for circular dependency - Move to another selector file
// If the footer is showing in request creation view, we should disable the Contact Support button
export const hideContactSupportButtonSelector = createSelector(
  // refactor so we don't pass state as a selector value and so that we pass showStickyFooterSelector as an actual selector
  [isRequestClosedSelector, currentUITabViewSelector, (state: IAppState) => state],
  (isRequestClosed: boolean, currentUITabView: UITabView, state: IAppState) =>
    showStickyFooterSelector(state, isRequestClosed) &&
      currentUITabView === UITabView.requestCreation &&
    getCurrentServiceRequestPageType(state.ContactState.ModalitySectionState) !== ServiceRequestPageType.SignInSection || state.ContactState.SharedState.isCMCRequestCreationDeeplink
);

export const additionalRequestCreationFieldsSelector = createSelector([sourceAppSelector, eduProductIdSelector],
  (sourceApp: SourceApps, eduProductId: string): InputField[] => {
    const inputFields: InputField[] = [];
    if (sourceApp === SourceApps.Edu) {
      const productSpecificFields = EduConfig.products[eduProductId]?.additionalFields ?? [];
      for (const fieldId of [...EduConfig.commonFields, ...productSpecificFields]) {
        if (EduConfig.inputFields[fieldId]) {
          inputFields.push(EduConfig.inputFields[fieldId]);
        }
      };
    }

    return inputFields;
});

export const showUserImpactAndRoleDropdownSelector = createSelector([sourceAppSelector, modalitySectionStateSelector],
  (sourceApp: SourceApps, modalityState: ModalitySectionState): boolean => {
    if (sourceApp !== SourceApps.Teams && sourceApp !== SourceApps.Teams2) {
      return false;
    }

    const userContextObj = getUserContext();
    const teamsTenantSKU = (userContextObj && userContextObj.TeamsTenantSKU) ? userContextObj.TeamsTenantSKU.toLowerCase() : null;
    const isEduTenant = teamsTenantSKU === 'edu' || teamsTenantSKU === 'education';
    return modalityState.isUserImpactAndRoleDropdownEnabled && isEduTenant;
});
